import { useEffect, useRef, useCallback, useState } from 'react';

const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
] as const;

// Heartbeat interval to check timeout even when visibility API doesn't fire
// (e.g., when browser window is minimized on macOS)
const HEARTBEAT_INTERVAL = 30 * 1000; // 30 seconds

// localStorage key for persisting last activity timestamp
const LAST_ACTIVITY_KEY = 'medley_last_activity';

// MED-1383: the auth store's persisted key. Only the API client removes it,
// when a refresh fails, so a removed value means every tab is signed out.
// Any other write to it is not a logout: Zustand writes it on every set().
const AUTH_STORAGE_KEY = 'auth-storage';

// MED-1383: the explicit cross-tab logout signal, { userId, at }. Only
// logout() and the API client's clearAuthAndRedirect write it.
const LOGOUT_SIGNAL_KEY = 'medley_logout';

interface UseIdleTimeoutOptions {
  /** Timeout in milliseconds before logout (default: 60 minutes) */
  timeout?: number;
  /** Warning time in milliseconds before logout (default: 5 minutes) */
  warningTime?: number;
  /** Callback when timeout is reached */
  onTimeout: () => void;
  /** Callback when warning period starts (optional) */
  onWarning?: () => void;
  /** Whether the hook is active (should be false when not authenticated) */
  enabled?: boolean;
  /**
   * MED-1383: the base user of this tab (the admin when impersonating). A
   * logout signal from another tab logs this tab out only for this user.
   */
  userId?: string | null;
  /**
   * MDF-258: stop the timer without ending the idle period, e.g. on the magic
   * link page. Unlike enabled=false, resuming does not start a fresh window:
   * the timer goes on from the stored last activity.
   */
  paused?: boolean;
}

interface UseIdleTimeoutReturn {
  /** Whether the warning period is active */
  isWarning: boolean;
  /** Remaining time in milliseconds */
  remainingTime: number;
  /** Reset the idle timer manually */
  resetTimer: () => void;
}

const DEFAULT_TIMEOUT = 60 * 60 * 1000; // 60 minutes
const DEFAULT_WARNING_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Get the last activity timestamp from localStorage, or null if not set
 */
function readStoredLastActivity(): number | null {
  try {
    const stored = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (stored) {
      const timestamp = parseInt(stored, 10);
      if (!isNaN(timestamp)) {
        return timestamp;
      }
    }
  } catch {
    // localStorage might be unavailable (e.g., private browsing)
  }
  return null;
}

/**
 * Get the last activity timestamp from localStorage, or current time if not set
 */
function getStoredLastActivity(): number {
  return readStoredLastActivity() ?? Date.now();
}

/**
 * MED-1383: tell the other tabs that this user logged out. userId is the base
 * user of the tab: the admin when the tab impersonates someone. The time makes
 * each write a new value, so the storage event fires every time.
 */
export function signalLogoutToOtherTabs(userId: string | null | undefined): void {
  if (!userId) return;
  try {
    localStorage.setItem(LOGOUT_SIGNAL_KEY, JSON.stringify({ userId, at: Date.now() }));
  } catch {
    // localStorage might be unavailable
  }
}

/** MED-1383: the user id in a logout signal, or null if it is unreadable. */
function readLogoutSignalUserId(value: string | null): string | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as { userId?: unknown };
    return typeof parsed?.userId === 'string' ? parsed.userId : null;
  } catch {
    return null;
  }
}

/**
 * Persist the last activity timestamp to localStorage
 */
export function setStoredLastActivity(timestamp: number): void {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, timestamp.toString());
  } catch {
    // localStorage might be unavailable
  }
}

/**
 * Clear the last activity timestamp from localStorage (on logout)
 */
export function clearStoredLastActivity(): void {
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch {
    // localStorage might be unavailable
  }
}

export function useIdleTimeout({
  timeout = DEFAULT_TIMEOUT,
  warningTime = DEFAULT_WARNING_TIME,
  onTimeout,
  onWarning,
  enabled = true,
  userId = null,
  paused = false,
}: UseIdleTimeoutOptions): UseIdleTimeoutReturn {
  const [isWarning, setIsWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(timeout);

  const timeoutRef = useRef<number | null>(null);
  const warningTimeoutRef = useRef<number | null>(null);
  const countdownRef = useRef<number | null>(null);
  const heartbeatRef = useRef<number | null>(null);
  const lastActivityRef = useRef<number>(getStoredLastActivity());
  const hasTimedOutRef = useRef<boolean>(false);
  const initialCheckDoneRef = useRef<boolean>(false);
  // MDF-258: set while paused, so the next resume reads the stored stamp.
  const wasPausedRef = useRef<boolean>(false);
  // MED-1298: ref twin of isWarning so the activity handler (a stable closure)
  // can check it synchronously — while the warning modal is showing, incidental
  // activity must not reset the timer, dismiss the modal, or touch the
  // persisted timestamp (AC1–AC3). Only setWarningState may write either.
  const isWarningRef = useRef<boolean>(false);

  // Store all config in refs so the effect doesn't depend on callback identity
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;
  const onWarningRef = useRef(onWarning);
  onWarningRef.current = onWarning;
  const timeoutValRef = useRef(timeout);
  timeoutValRef.current = timeout;
  const warningTimeValRef = useRef(warningTime);
  warningTimeValRef.current = warningTime;
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      window.clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownRef.current) {
      window.clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (heartbeatRef.current) {
      window.clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const setWarningState = useCallback((value: boolean) => {
    isWarningRef.current = value;
    setIsWarning(value);
  }, []);

  // MED-1383: every tab writes LAST_ACTIVITY_KEY on activity, but each tab
  // timed itself from its own ref, so an idle tab logged out a person who was
  // working in another tab. Take the newest stamp before any timing decision.
  // Returns true when the stored stamp was newer than this tab's.
  const adoptNewerActivity = useCallback((): boolean => {
    const stored = readStoredLastActivity();
    if (stored !== null && stored > lastActivityRef.current) {
      lastActivityRef.current = stored;
      return true;
    }
    return false;
  }, []);

  // Set below. Lets the timer callbacks reschedule without a circular
  // dependency between fireTimeout and scheduleFromLastActivity.
  const resumeFromLastActivityRef = useRef<() => void>(() => undefined);

  // MED-1298: the single logout path. Guarded so the main timeout, the
  // countdown hitting zero, the heartbeat, and the visibility handler can all
  // race to it safely.
  const logOutNow = useCallback(() => {
    if (hasTimedOutRef.current) return;
    hasTimedOutRef.current = true;
    clearAllTimers();
    onTimeoutRef.current();
  }, [clearAllTimers]);

  // The timer path. MED-1383: the timer was set from an older stamp, so
  // check the shared stamp first. Activity in another tab reschedules
  // instead of logging out.
  const fireTimeout = useCallback(() => {
    if (hasTimedOutRef.current) return;
    adoptNewerActivity();
    if (Date.now() - lastActivityRef.current < timeoutValRef.current) {
      resumeFromLastActivityRef.current();
      return;
    }
    logOutNow();
  }, [adoptNewerActivity, logOutNow]);

  const startCountdown = useCallback(() => {
    // MED-1298: idempotent — the heartbeat used to call this every 30s during
    // the warning window, stacking one-second intervals. Only one countdown
    // interval may run at a time.
    if (countdownRef.current) {
      window.clearInterval(countdownRef.current);
    }
    countdownRef.current = window.setInterval(() => {
      // MED-1383: activity in another tab ends or shortens this warning.
      if (adoptNewerActivity()) {
        resumeFromLastActivityRef.current();
        return;
      }
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = Math.max(0, timeoutValRef.current - elapsed);
      setRemainingTime(remaining);
      if (remaining <= 0) {
        // MED-1298 (AC6): the countdown reaching zero must log out, not just
        // clear timers — in a throttled background tab this tick can land
        // before the main setTimeout, and clearing it without firing stranded
        // the modal at 0:00 with no logout.
        fireTimeout();
      }
    }, 1000);
  }, [adoptNewerActivity, fireTimeout]);

  // MED-1298: enter the warning window exactly once per idle period. Repeat
  // calls (heartbeat ticks, visibility returns) are no-ops so onWarning fires
  // once and the countdown isn't restarted.
  const enterWarning = useCallback(() => {
    if (isWarningRef.current || hasTimedOutRef.current) return;
    // MED-1383: do not warn if another tab saw activity since the timer was set.
    adoptNewerActivity();
    const elapsed = Date.now() - lastActivityRef.current;
    if (elapsed < timeoutValRef.current - warningTimeValRef.current) {
      resumeFromLastActivityRef.current();
      return;
    }
    setWarningState(true);
    onWarningRef.current?.();
    startCountdown();
  }, [adoptNewerActivity, setWarningState, startCountdown]);

  /**
   * MED-1298 (AC7/AC9): schedule the warning and logout timers from the
   * CURRENT lastActivity timestamp rather than restamping it. On a reload or
   * remount this restores the in-flight idle period — including landing
   * straight back in the warning window with the correct remaining time —
   * instead of silently granting a fresh timeout (which is how reloading
   * used to extend the session).
   */
  const scheduleFromLastActivity = useCallback(() => {
    // Clear pending timers but keep the heartbeat running
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      window.clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownRef.current) {
      window.clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    const elapsed = Date.now() - lastActivityRef.current;
    const remaining = Math.max(0, timeoutValRef.current - elapsed);
    setRemainingTime(remaining);

    if (remaining <= 0) {
      // AC8: logout wins over showing a countdown for time already elapsed.
      fireTimeout();
      return;
    }

    const warningDelay = timeoutValRef.current - warningTimeValRef.current;
    if (elapsed >= warningDelay) {
      enterWarning();
    } else if (warningDelay > 0) {
      warningTimeoutRef.current = window.setTimeout(
        enterWarning,
        warningDelay - elapsed
      );
    }

    timeoutRef.current = window.setTimeout(fireTimeout, remaining);
  }, [enterWarning, fireTimeout]);

  /**
   * MED-1383: reschedule after this tab adopts a newer stamp from another
   * tab. If the new stamp is outside the warning window, close the warning.
   * If the warning stays, restart its countdown, which
   * scheduleFromLastActivity clears. This is not activity in this tab, so it
   * does not write the stamp.
   */
  const resumeFromLastActivity = useCallback(() => {
    const elapsed = Date.now() - lastActivityRef.current;
    if (isWarningRef.current && elapsed < timeoutValRef.current - warningTimeValRef.current) {
      setWarningState(false);
    }
    const stillWarning = isWarningRef.current;
    scheduleFromLastActivity();
    if (stillWarning && !hasTimedOutRef.current) {
      startCountdown();
    }
  }, [scheduleFromLastActivity, setWarningState, startCountdown]);
  resumeFromLastActivityRef.current = resumeFromLastActivity;

  // resetTimer reads config from refs — no deps on timeout/warningTime/enabled.
  // This is the ONLY place activity extends the session: it restamps (and
  // persists) lastActivity, then schedules from it.
  const resetTimer = useCallback(() => {
    setWarningState(false);
    hasTimedOutRef.current = false;

    const now = Date.now();
    lastActivityRef.current = now;
    setStoredLastActivity(now);

    scheduleFromLastActivity();
  }, [scheduleFromLastActivity, setWarningState]);

  // Track whether we were previously disabled (logged out)
  const wasDisabledRef = useRef<boolean>(!enabled);

  // Set up event listeners and heartbeat — only depends on `enabled`
  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      setWarningState(false);
      setRemainingTime(timeoutValRef.current);
      hasTimedOutRef.current = false;
      initialCheckDoneRef.current = false;
      wasDisabledRef.current = true;
      lastActivityRef.current = Date.now();
      return;
    }

    // MDF-258: paused keeps the idle period. No timers, no listeners, and
    // the fresh login reset below is not armed.
    // shortcut: a logout in another tab is not heard while paused; the
    // tab's access token (15 minutes) then fails refresh. Listen here too
    // if the verify page ever holds a session for long.
    if (paused) {
      clearAllTimers();
      setWarningState(false);
      wasPausedRef.current = true;
      return;
    }

    // If we're enabling after being disabled (fresh login), reset the timestamp
    if (wasDisabledRef.current) {
      wasDisabledRef.current = false;
      wasPausedRef.current = false;
      lastActivityRef.current = Date.now();
      setStoredLastActivity(Date.now());
    } else if (wasPausedRef.current) {
      // MDF-258: after a pause, go on from the stored stamp. Fail closed: a
      // missing stamp counts as expired. A magic link login writes a fresh one.
      wasPausedRef.current = false;
      lastActivityRef.current = readStoredLastActivity() ?? 0;
    }

    const checkAndHandleTimeout = () => {
      if (hasTimedOutRef.current) return;
      // MED-1383: another tab saw activity, so reschedule from that stamp.
      if (adoptNewerActivity()) {
        resumeFromLastActivity();
        return;
      }
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= timeoutValRef.current) {
        // AC8: total inactivity has passed the full timeout (e.g. while the
        // tab was hidden/throttled) — logout wins over resuming a countdown.
        fireTimeout();
      } else if (elapsed >= timeoutValRef.current - warningTimeValRef.current) {
        // AC7: back inside the warning window — correct the displayed time
        // immediately, and (idempotently) make sure the warning is showing.
        setRemainingTime(timeoutValRef.current - elapsed);
        enterWarning();
      }
    };

    // On initial mount, check if session already expired (bookmark return scenario)
    if (!initialCheckDoneRef.current) {
      initialCheckDoneRef.current = true;
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= timeoutValRef.current) {
        hasTimedOutRef.current = true;
        onTimeoutRef.current();
        return;
      }
    }

    // MED-1298 (AC9): restore the in-flight idle period from the persisted
    // timestamp instead of restamping it. Mounting is not user activity — the
    // old resetTimer() here is what let a reload during the warning silently
    // extend the session. (A fresh login stamped a new timestamp above, so
    // this still yields a full window there.)
    scheduleFromLastActivity();

    // Activity event listeners.
    // MED-1298 (AC1–AC3): once the warning is showing, incidental activity —
    // including the mouse movement of reaching for the modal's buttons — no
    // longer resets the timer or dismisses the modal. Only the modal's
    // explicit "Stay Logged In" (resetTimer) or "Log Out Now" ends the
    // warning. Suppressing the reset also suppresses the localStorage
    // timestamp write, so a reload can't resurrect the dismissal (AC9).
    const handleActivity = () => {
      if (isWarningRef.current) return;
      resetTimer();
    };
    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Visibility change handler
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAndHandleTimeout();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // MED-1383: the storage event fires in every other tab of this origin.
    // A newer activity stamp reschedules this tab. A logout in another tab
    // runs the logout path here too, so all tabs go to the login page
    // together. A logout is a logout signal for this tab's user, a removed
    // auth-storage, or a cleared storage.
    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea !== localStorage) return;
      if (event.key === LAST_ACTIVITY_KEY) {
        if (adoptNewerActivity()) {
          resumeFromLastActivity();
        }
      } else if (event.key === LOGOUT_SIGNAL_KEY) {
        const signalUserId = readLogoutSignalUserId(event.newValue);
        if (signalUserId && signalUserId === userIdRef.current) {
          logOutNow();
        }
      } else if (
        event.key === null ||
        (event.key === AUTH_STORAGE_KEY && event.newValue === null)
      ) {
        logOutNow();
      }
    };
    window.addEventListener('storage', handleStorage);

    // Heartbeat
    heartbeatRef.current = window.setInterval(checkAndHandleTimeout, HEARTBEAT_INTERVAL);

    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, [
    enabled,
    paused,
    adoptNewerActivity,
    clearAllTimers,
    enterWarning,
    fireTimeout,
    logOutNow,
    resetTimer,
    resumeFromLastActivity,
    scheduleFromLastActivity,
    setWarningState,
  ]);

  return {
    isWarning,
    remainingTime,
    resetTimer,
  };
}

/** Format remaining time as MM:SS */
export function formatRemainingTime(ms: number): string {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
